import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useGlobalStore } from "../../../pages/store/globalStore";
import InputFieldsController from "../InputFieldsController/InputFieldsController";
import {
  FieldProps,
  FieldVariant,
  SectionLayout,
  SectionVariant,
  handleChange as coreHandleChange,
  handleBlur as coreHandleBlur,
  hasRequiredValidation,
  handleApiDropdownSelection,
  validateField,
  shouldFieldBeVisibleForDefaults,
  clearIfAutoApplied,
  applyDefaultIfEligible
} from "../InputFieldsController/InputFieldsController.utils";
import { isEqual } from "lodash";
import styles from "./CygnusForm.module.css";
import CustomButton from "../../../pages/reusable_components/custom_button_component/Button";
import * as Icon from "react-bootstrap-icons";
import SectionWrapper from "./SectionWrapper";
import { applyNameFormat } from "../InputFieldsController/InputFieldsController.utils"
import { fromPickerDate } from "../DateInput/DateInput.utils";
import SystemFieldDrawer from "./components/SystemFieldDrawer/SystemFieldDrawer";
import {
  isUnmappedSystemField,
  toCategoryOptions,
} from "./components/SystemFieldDrawer/SystemFieldDrawer.utils";
import {
  getAllSystemFields,
  getFieldCategoryOptions,
} from "./components/SystemFieldDrawer/SystemFieldDrawer.service";
import {
  ReferenceDataOption,
  SystemField,
} from "./components/SystemFieldDrawer/SystemFieldDrawer.types";

interface CygnusFormProps {
  formId: string;
  fields: FieldProps[];
  disabled?: boolean;
  onFieldsChange: (cygnusFormFields: FieldProps[]) => void;
  onValidationChange: (isValid: boolean) => void;
  onDemandValidation?: boolean;
  onDemandValidationComplete?: (isValid: boolean) => void;
  onFieldEvent?: (evt: {
    eventKey?: string;
    field: FieldProps;
    value: any;
    allFields: FieldProps[];
    formId: string;
  }) => void;
  onDirtyChange?: (dirty: {
    ids: string[];
    byId: Record<string, true>;
  }) => void; // optional push
  provideDirtyApi?: (api: {
    get: () => string[]; // pull
    has: (id: string) => boolean;
    size: () => number;
    clear: () => void; // reset
  }) => void;
  resetDirtyKey?: any;
  onDropdownsLoaded?: () => void;
  fieldVariant?: FieldVariant;
  sectionVariant?: SectionVariant;
  sectionLayout?: SectionLayout;
  intakeRules?: string;
  enableSystemFieldManagement?: boolean;
}

type IntakeRuleCondition = {
  var?: string;
  op?:
    | "eq"
    | "neq"
    | "eq_field"
    | "neq_field"
    | "contains"
    | "in"
    | "not_empty"
    | "empty";
  value?: any;
  case_insensitive?: boolean;
};

type IntakeRule = {
  rule_name?: string;
  execution_order?: number;
  default?: boolean;
  when?: {
    all?: IntakeRuleCondition[];
    any?: IntakeRuleCondition[];
  };
  then?: Record<string, any>;
};

/**
 * `CygnusForm` is a dynamic form renderer that accepts an initial schema of cygnusFormFields,
 * manages internal state and validation, and emits updated values and validation status to the parent.
 *
 * @component
 * @param {Object} props - The component props
 * @param {FieldProps[]} props.fields - Initial list of form cygnusFormFields to render and manage
 * @param {(cygnusFormFields: FieldProps[]) => void} [props.onFieldsChange] - Callback to return updated field values to the parent
 * @param {(isValid: boolean) => void} [props.onValidationChange] - Callback to notify parent if the form is fully valid
 * @returns {JSX.Element} Rendered form element with all cygnusFormFields and validation behavior
 */

const CygnusForm: React.FC<CygnusFormProps> = ({
  formId,
  fields,
  disabled = false,
  onFieldsChange,
  onValidationChange,
  onDemandValidation,
  onDemandValidationComplete,
  onFieldEvent,
  onDirtyChange,
  provideDirtyApi,
  resetDirtyKey,
  onDropdownsLoaded,
  fieldVariant,
  sectionVariant,
  sectionLayout,
  intakeRules,
  enableSystemFieldManagement = false,
}) => {
  const toBool = (v: unknown): boolean => {
    if (typeof v === "boolean") return v;
    if (typeof v === "string") return v.trim().toLowerCase() === "true";
    return false;
  };

  const seedFieldsForInitialRender = (fields: FieldProps[]) => {
    const normalized = normalizeFields(fields);
    return normalized.map((f) => {
      const visible = shouldFieldBeVisibleForDefaults(f, normalized);
      if (!visible) {
        return clearIfAutoApplied({
          ...f,
          error: "",
          isValid: undefined,
        });
      }
      return applyDefaultIfEligible(f);
    });
  };

  /**
 * normalizeFields (PURE)
 *
 * Purpose:
 * - Keep field shape stable (no null/undefined landmines).
 * - DO NOT apply defaultValue here.
 * - DO NOT clear values here.
 *
 * Why:
 * - Defaulting and dependency-branch clearing must happen deterministically
 *   when state changes (inside handleChange / init seed), not during render.
 * - Doing defaults in render-like normalization creates "ghost re-defaults":
 *   user resets -> normalize re-applies -> UI looks possessed.
 */
  const normalizeFields = (fs: FieldProps[]): FieldProps[] => {
    return fs.map((f) => {
      const next: FieldProps = { ...f };

      if (typeof next.show === "undefined") next.show = true;

      // --- Normalize boolean-ish fields (critical for visibility on first load) ---
      if (next.type === "checkbox" || next.type === "radio" || next.type === "slider") {
        if (next.value === "true" || next.value === "1" || next.value === 1) next.value = true;
        else if (next.value === "false" || next.value === "0" || next.value === 0) next.value = false;
      }

      if (next.value === null || typeof next.value === "undefined") {
        next.value = "";
      }

      if (next.type === "select" && (next.value === "" || next.value === null)) {
        next.selectedOption = undefined;
      }

      next.autoValueApplied = next.autoValueApplied ?? false;
      next.userCleared = next.userCleared ?? false;

      if (
        next.type === "date" &&
        next.defaultToToday === true &&
        (next.value === "" || next.value == null) &&
        !next.userCleared &&
        !next.autoValueApplied
      ) {
        next.value = fromPickerDate(next, new Date());
        next.autoValueApplied = true;
      }
      return next;
    });
  };

  const [cygnusFormFields, setCygnusFormFields] = useState<FieldProps[]>(
    seedFieldsForInitialRender(fields)
  );
  const [showSystemFieldDrawer, setShowSystemFieldDrawer] = useState(false);
  const [systemFieldDrawerFieldId, setSystemFieldDrawerFieldId] = useState("");
  const [systemFields, setSystemFields] = useState<SystemField[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<ReferenceDataOption[]>([]);
  const [systemFieldDataLoading, setSystemFieldDataLoading] = useState(false);

  const loadSystemFieldData = async () => {
    setSystemFieldDataLoading(true);
    try {
      const [allFields, categories] = await Promise.all([
        getAllSystemFields(),
        getFieldCategoryOptions(),
      ]);
      setSystemFields(allFields);
      setCategoryOptions(toCategoryOptions(categories));
      return allFields;
    } finally {
      setSystemFieldDataLoading(false);
    }
  };

  const refreshSystemFields = async () => {
    const allFields = await getAllSystemFields();
    setSystemFields(allFields);
    return allFields;
  };

  useEffect(() => {
    if (!enableSystemFieldManagement) return;
    loadSystemFieldData();
  }, [enableSystemFieldManagement]);

  const normalizeRuleKey = (value: any) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const getFieldCodeKeys = (field: FieldProps) =>
    [
      field.allfields_code,
      field.code,
    ]
      .filter(Boolean)
      .map(normalizeRuleKey);

  const getFieldFallbackKeys = (field: FieldProps) =>
    [
      field.id,
      field.label,
      field.customLabel,
    ]
      .filter(Boolean)
      .map(normalizeRuleKey);

  const findFieldByRuleName = (allFields: FieldProps[], name?: string) => {
    const key = normalizeRuleKey(name);
    if (!key) return undefined;

    return (
      allFields.find((field) => getFieldCodeKeys(field).includes(key)) ??
      allFields.find((field) => getFieldFallbackKeys(field).includes(key))
    );
  };

  const isEmptyRuleValue = (value: any) =>
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0);

  const isBooleanLikeField = (field: FieldProps) =>
    field.type === "checkbox" || field.type === "radio" || field.type === "slider";

  const getRuleValue = (allFields: FieldProps[], name?: string): any => {
    const field = findFieldByRuleName(allFields, name);
    const key = normalizeRuleKey(name);

    if (field && (key !== "clientname" || !isEmptyRuleValue(field.value))) {
      return field.value;
    }

    if (key === "clientname") {
      const first =
        findFieldByRuleName(allFields, "ClientFirstName") ??
        findFieldByRuleName(allFields, "Client First Name") ??
        findFieldByRuleName(allFields, "FirstName") ??
        findFieldByRuleName(allFields, "First Name");
      const last =
        findFieldByRuleName(allFields, "ClientLastName") ??
        findFieldByRuleName(allFields, "Client Last Name") ??
        findFieldByRuleName(allFields, "LastName") ??
        findFieldByRuleName(allFields, "Last Name");

      return [first?.value, last?.value]
        .map((part) => String(part ?? "").trim())
        .filter(Boolean)
        .join(" ");
    }

    return "";
  };

  const getRuleComparableValues = (allFields: FieldProps[], name?: string) => {
    const field = findFieldByRuleName(allFields, name);
    if (!field) return [getRuleValue(allFields, name)];

    const selectedOption = field.selectedOption as
      | { label?: any; value?: any }
      | undefined;

    return [field.value, selectedOption?.value, selectedOption?.label].filter(
      (value) => value !== undefined && value !== null
    );
  };

  const normalizeComparableValue = (value: any, caseInsensitive?: boolean) => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    return caseInsensitive ? trimmed.toLowerCase() : trimmed;
  };

  const evaluateIntakeCondition = (
    condition: IntakeRuleCondition,
    allFields: FieldProps[]
  ) => {
    const op = condition.op ?? "eq";
    const actualValues = getRuleComparableValues(allFields, condition.var);
    const actual = actualValues[0];
    const expected = condition.value;

    if (op === "not_empty") return !isEmptyRuleValue(actual);
    if (op === "empty") return isEmptyRuleValue(actual);

    const normalizedActualValues = actualValues.map((value) =>
      normalizeComparableValue(value, condition.case_insensitive)
    );

    if (op === "eq_field" || op === "neq_field") {
      const expectedFieldValues = getRuleComparableValues(
        allFields,
        String(condition.value ?? "")
      ).map((value) =>
        normalizeComparableValue(value, condition.case_insensitive)
      );

      const hasMatch = normalizedActualValues.some((actualValue) =>
        expectedFieldValues.some((expectedValue) => actualValue === expectedValue)
      );

      return op === "eq_field" ? hasMatch : !hasMatch;
    }

    if (op === "contains") {
      const expectedText = String(
        normalizeComparableValue(expected, condition.case_insensitive) ?? ""
      );
      return normalizedActualValues.some((value) =>
        String(value ?? "").includes(expectedText)
      );
    }

    if (op === "in") {
      const expectedValues = Array.isArray(expected) ? expected : [expected];
      return expectedValues.some((item) =>
        normalizedActualValues.some(
          (actualValue) =>
            normalizeComparableValue(item, condition.case_insensitive) ===
            actualValue
        )
      );
    }

    const normalizedExpected = normalizeComparableValue(
      expected,
      condition.case_insensitive
    );

    if (op === "neq") {
      return normalizedActualValues.every(
        (actualValue) => actualValue !== normalizedExpected
      );
    }

    return normalizedActualValues.some(
      (actualValue) => actualValue === normalizedExpected
    );
  };

  const evaluateIntakeRule = (rule: IntakeRule, allFields: FieldProps[]) => {
    if (rule.default) return true;

    const all = rule.when?.all ?? [];
    const any = rule.when?.any ?? [];

    const allMatches =
      all.length === 0 ||
      all.every((condition) => evaluateIntakeCondition(condition, allFields));
    const anyMatches =
      any.length === 0 ||
      any.some((condition) => evaluateIntakeCondition(condition, allFields));

    return allMatches && anyMatches;
  };

  const renderRuleTemplate = (template: any, allFields: FieldProps[]) => {
    if (typeof template !== "string") return template;

    return template.replace(/\{([^}]+)\}/g, (_match, token) =>
      String(getRuleValue(allFields, token) ?? "")
    );
  };

  const parseIntakeRules = (rules?: string): IntakeRule[] => {
    if (!rules?.trim()) return [];

    try {
      const parsed = JSON.parse(rules);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      console.warn("Invalid intakeRules JSON:", error);
      return [];
    }
  };

  const applyIntakeRules = (allFields: FieldProps[]) => {
    const rules = parseIntakeRules(intakeRules).sort(
      (a, b) => (a.execution_order ?? 0) - (b.execution_order ?? 0)
    );
    if (rules.length === 0) return allFields;

    const fieldAssignments = new Map<string, Record<string, any>>();

    rules.forEach((rule) => {
      if (!evaluateIntakeRule(rule, allFields)) return;

      Object.entries(rule.then ?? {}).forEach(([targetName, template]) => {
        const targetField = findFieldByRuleName(allFields, targetName);
        if (!targetField) return;

        if (!fieldAssignments.has(targetField.id)) {
          fieldAssignments.set(targetField.id, {});
        }
        const assignments = fieldAssignments.get(targetField.id)!;

        if (template !== null && typeof template === "object" && !Array.isArray(template)) {
          Object.entries(template as Record<string, any>).forEach(([prop, val]) => {
            if (!(prop in assignments)) assignments[prop] = val;
          });
        } else {
          if (!("value" in assignments)) assignments["value"] = template;
        }
      });
    });

    return allFields.map((field) => {
      const assignments = fieldAssignments.get(field.id) ?? {};
      const ruleModified = field._ruleModified ?? {};
      const hasAssignments = Object.keys(assignments).length > 0;
      const hasPriorState = Object.keys(ruleModified).length > 0;

      if (!hasAssignments && !hasPriorState) return field;

      let result = { ...field };
      const newRuleModified = { ...ruleModified };

      const ruleModifiedAny = ruleModified as Record<string, any>;
      Object.keys(ruleModified).forEach((prop) => {
        if (!(prop in assignments)) {
          (result as any)[prop] = ruleModifiedAny[prop];
          delete (newRuleModified as Record<string, any>)[prop];
        }
      });

      const manuallyEdited = dirtyRef.current[String(field.id)] === true;

      Object.entries(assignments).forEach(([prop, newVal]) => {
        if (prop === "value") {
          const isRuleOwned =
            "_ruleAppliedValue" in field &&
            isEqual(field.value, field._ruleAppliedValue);

          const effectiveReadOnly =
            "readOnly" in assignments ? assignments.readOnly : field.readOnly;
          const effectiveDisabled =
            "disabled" in assignments ? assignments.disabled : field.disabled;
          const isLocked = effectiveReadOnly === true || effectiveDisabled === true;

          const canOverwrite =
            !manuallyEdited &&
            (isEmptyRuleValue(field.value) ||
              isRuleOwned ||
              isLocked ||
              (isBooleanLikeField(field) && field.value === false));
          if (!canOverwrite) return;
          if (!("value" in newRuleModified)) newRuleModified["value"] = field.value;
          const renderedValue = typeof newVal === "string"
            ? renderRuleTemplate(newVal, allFields)
            : newVal;
          result.value = renderedValue;
          result._ruleAppliedValue = renderedValue;
          result.autoValueApplied = false;
          result.userCleared = false;
        } else {
          if (!(prop in newRuleModified)) {
            (newRuleModified as Record<string, any>)[prop] = (field as any)[prop];
          }
          (result as any)[prop] = newVal;
        }
      });

      result._ruleModified = Object.keys(newRuleModified).length > 0
        ? newRuleModified
        : undefined;

      return result;
    });
  };

  // Pagination view
  const [viewMode, setViewMode] = useState<"all" | "paginated">("all");
  const [currentPage, setCurrentPage] = useState<number>(0);

  const pages: FieldProps[][] = [];
  let currentPageGroup: FieldProps[] = [];

  cygnusFormFields.forEach((field) => {
    if (field.pageBreak && currentPageGroup.length > 0) {
      pages.push(currentPageGroup);
      currentPageGroup = [];
    }
    currentPageGroup.push(field);
  });
  if (currentPageGroup.length > 0) {
    pages.push(currentPageGroup);
  }

  const handlePrev = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, pages.length - 1));
  };

  useEffect(() => {
    if (currentPage >= pages.length) {
      setCurrentPage(pages.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages.length]);

  // Use ref to store previous cygnusFormFields passed to parent to avoid infinite loop
  const lastEmittedRef = useRef<FieldProps[] | null>(null); // what we sent to parent last
  const isHydratingFromPropsRef = useRef(false); // we're applying props → don't emit

  // Sync local state when `fields` set REF to keep track of changes
  useEffect(() => {
    const next = seedFieldsForInitialRender(fields);
    
    // If parent just echoed what we emitted, ignore
    if (lastEmittedRef.current && isEqual(next, lastEmittedRef.current)) {
      // we've already got this, don't overwrite local or emit
      return;
    }

    // Preserve already-loaded dropdown options when parent re-sends fields without options
    setCygnusFormFields((prev) => {
      if (isEqual(next, prev)) return prev;

      const prevById = new Map(prev.map((f) => [f.id, f]));

      const merged = next.map((incoming) => {
        const local = prevById.get(incoming.id);
        if (!local) return incoming;

        const localHasOptions = Array.isArray(local.options) && local.options.length > 0;
        const incomingHasOptions = Array.isArray(incoming.options) && incoming.options.length > 0;

        // Preserve locally-loaded options when parent sends fields with no options
        if (localHasOptions && !incomingHasOptions) {
          return {
            ...incoming,
            options: local.options,
            allOptions: local.allOptions ?? local.options,
            apiConfig: {
              ...incoming.apiConfig,
              fetchedOptions: local.apiConfig?.fetchedOptions ?? incoming.apiConfig?.fetchedOptions,
              isFetching: incoming.apiConfig?.isFetching ?? local.apiConfig?.isFetching,
            },
          };
        }

        return incoming;
      });

      isHydratingFromPropsRef.current = true;
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]); // ← no touching lastEmittedRef here

  useEffect(() => {
    if (!intakeRules?.trim()) return;

    setCygnusFormFields((prev) => {
      const next = applyIntakeRules(prev);
      return isEqual(next, prev) ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cygnusFormFields, intakeRules]);

  useEffect(() => {
    if (!onDemandValidation) return;

    const validatedFields = cygnusFormFields.map((f) => {
      if (!shouldFieldBeVisible(f, cygnusFormFields)) {
        return { ...f, isValid: true, error: "" };
      }

      if (!f.validation || f.validation.length === 0) {
        return { ...f, isValid: true, error: "" };
      }

      return validateField(f);
    });

    setCygnusFormFields(validatedFields);

    const isValid = validatedFields.every((f) => {
      if (!shouldFieldBeVisible(f, validatedFields)) return true;
      if (!f.validation || f.validation.length === 0) return true;
      return f.isValid !== false;
    });

    onValidationChange?.(isValid);
    onDemandValidationComplete?.(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDemandValidation]);

  // Sync updates to parent only if `cygnusFormFields` actually changed from last emitted value
  useEffect(() => {
    // If we just applied props, clear the flag and don't emit
    if (isHydratingFromPropsRef.current) {
      isHydratingFromPropsRef.current = false;
      return;
    }

    // If nothing changed vs last emitted, do nothing
    if (
      lastEmittedRef.current &&
      isEqual(cygnusFormFields, lastEmittedRef.current)
    ) {
      return;
    }

    // Emit updated fields
    onFieldsChange?.(cygnusFormFields);

    const isValid = cygnusFormFields.every((f) => {
      if (!shouldFieldBeVisible(f, cygnusFormFields)) return true;
      if (!hasRequiredValidation(f)) return true;
      return f.isValid !== false && f.value !== "" ;
    });
    onValidationChange?.(isValid);

    // Mark what we just sent so the next props echo can be ignored
    lastEmittedRef.current = cygnusFormFields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cygnusFormFields]);

  const mergeOptionsOnly = (prev: FieldProps[], working: FieldProps[]) => {
    if (!Array.isArray(working) || working.length === 0) return prev;

    const byId = new Map(working.map((f) => [f.id, f]));

    return prev.map((f) => {
      const w = byId.get(f.id);
      if (!w) return f;

      const wHasOptions =
        (Array.isArray(w.options) && w.options.length > 0) ||
        (Array.isArray(w.apiConfig?.fetchedOptions) && (w.apiConfig!.fetchedOptions as any[]).length > 0);
      if (!wHasOptions) return f;

      const nextOptions = w.options ?? f.options;
      const nextAllOptions = w.allOptions ?? f.allOptions;
      const wFetchedOptions = w.apiConfig?.fetchedOptions;
      let fetchedOptions = wFetchedOptions ?? f.apiConfig?.fetchedOptions;

      if (w.type === "select" && w.format && Array.isArray(fetchedOptions)) {
        const formatted = fetchedOptions.map((o: any) => ({
          ...o,
          label: applyNameFormat(String(o.label), w.format),
          value: applyNameFormat(String(o.value), w.format),
        }));
        fetchedOptions = formatted;
      }

      let nextValue;
      if (w.type === "select" && w.format && f.value) {
        nextValue = applyNameFormat(String(f.value), w.format);
      }

      // ------------------------------------------------------------
      // IMPORTANT CHANGE:
      // - For typeahead selects, DO NOT clear the value just because options
      //   haven't been loaded yet.
      // - Also, if options load but do not include the current value, inject
      //   a placeholder option so the UI can still display something.
      // ------------------------------------------------------------
      let finalOptions: any[] = Array.isArray(nextOptions) ? nextOptions : [];

      if (f.type === "select" && f.typeahead && f.value && !f.userCleared) {
        const hasSelected =
          finalOptions.length > 0 &&
          finalOptions.some((opt: any) => String(opt?.value) === String(f.value));

        if (!hasSelected) {
          // Use the best known label instead of showing only the raw id
          const bestLabel =
            (f.selectedOption as any)?.label ??
            (f.selectedOption as any)?.value ??
            String(f.value);

          finalOptions = [
            { value: String(f.value), label: String(bestLabel), id: String(f.value) },
            ...finalOptions,
          ];
        }

        const match = finalOptions.find(
          (opt: any) => String(opt?.value) === String(f.value)
        );

        return {
          ...f,
          options: finalOptions,
          allOptions: nextAllOptions ?? finalOptions,
          apiConfig: {
            ...f.apiConfig,
            ...w.apiConfig,
            fetchedOptions: fetchedOptions,
            apiKey: f.apiConfig?.apiKey,
          },
          // Keep value (never wipe during async loading)
          value: nextValue ?? f.value,
          // Ensure the selected value has a matching option so React-Select can display its correct label (even during async loading)
          selectedOption: match ?? f.selectedOption,
        };
      }

      // Default behavior for non-typeahead or non-select fields:
      const valueStillValid =
        f.type !== "select" ||
        (Array.isArray(finalOptions) &&
          finalOptions.some((opt: any) => String(opt?.value) === String(f.value)));

      return {
        ...f,
        options: finalOptions,
        allOptions: nextAllOptions,
        apiConfig: {
          ...f.apiConfig,
          ...w.apiConfig,
          fetchedOptions: fetchedOptions,
          apiKey: f.apiConfig?.apiKey,
        },
        value: nextValue ?? f.value,
      };
    });
  };

  // This is trigger for fetching data on form load
  const hasFetchedDropdowns = useRef(false);
  // Prime typeahead selects once (per form mount) for edit mode prefill
  const primedTypeaheadRef = useRef<Set<string>>(new Set());
  const isInitialHydrationRef = useRef(true);

  useEffect(() => {
    if (hasFetchedDropdowns.current || fields.length === 0) return;

    hasFetchedDropdowns.current = true;

    let workingCopy: FieldProps[] = [...fields];
    // console.log("CYGNUS FORM INPUT FIELDS: ", workingCopy);

    const fetchDropdowns = async () => {
      const fieldsByApiKey = new Map<string, FieldProps[]>();
      fields
        .filter((field) => field.apiConfig?.apiKey && !field.typeahead)
        .forEach((field) => {
          const key = field.apiConfig!.apiKey!;
          if (!fieldsByApiKey.has(key)) fieldsByApiKey.set(key, []);
          fieldsByApiKey.get(key)!.push(field);
        });

      const { dropdownOptionsCache } = useGlobalStore.getState();
      const uncachedGroups: FieldProps[][] = [];

      for (const groupFields of fieldsByApiKey.values()) {
        const apiKey = groupFields[0].apiConfig!.apiKey!;
        const cached = dropdownOptionsCache?.[apiKey];
        if (cached) {
          for (const field of groupFields) {
            workingCopy = workingCopy.map((f) =>
              f.id === field.id
                ? { ...f, apiConfig: { ...f.apiConfig, fetchedOptions: cached }, options: cached }
                : f
            );
          }
        } else {
          uncachedGroups.push(groupFields);
        }
      }

      const tasks = uncachedGroups.map(
        (groupFields) => () =>
          new Promise<void>((resolve) => {
            handleApiDropdownSelection(
              groupFields[0],
              groupFields[0].apiConfig!.apiKey!,
              workingCopy,
              (updated) => {
                const apiKey = groupFields[0].apiConfig!.apiKey!;
                const representative = updated.find((f) => f.id === groupFields[0].id);
                const fetchedOptions = representative?.apiConfig?.fetchedOptions;

                if (fetchedOptions) {
                  // Write to session cache so future form mounts skip the API call
                  const { dropdownOptionsCache, setGlobalOptions } = useGlobalStore.getState();
                  setGlobalOptions({
                    dropdownOptionsCache: { ...dropdownOptionsCache, [apiKey]: fetchedOptions },
                  });

                  if (groupFields.length > 1) {
                    const siblingIds = new Set(groupFields.slice(1).map((f) => f.id));
                    workingCopy = updated.map((f) =>
                      siblingIds.has(f.id)
                        ? { ...f, apiConfig: { ...f.apiConfig, fetchedOptions }, options: fetchedOptions }
                        : f
                    );
                  } else {
                    workingCopy = updated;
                  }
                } else {
                  workingCopy = updated;
                }
                resolve();
              }
            );
          })
      );

      // Execute sequentially
      for (const task of tasks) {
        await task();
      }

      // Apply final state update once
      setCygnusFormFields((prev) => mergeOptionsOnly(prev, workingCopy));

      onDropdownsLoaded?.();
      isInitialHydrationRef.current = false;
    };

    fetchDropdowns();
  }, [fields]);

  useEffect(() => {
    // Only run after initial dropdown load attempt has started
    if (!hasFetchedDropdowns.current) return;
    if (!Array.isArray(cygnusFormFields) || cygnusFormFields.length === 0) return;

    // Find typeahead selects that already have a saved value but no options loaded yet
    const targets = cygnusFormFields.filter((f) => {
      return (
        f.type === "select" &&
        f.typeahead === true &&
        !!f.apiConfig?.apiKey &&
        !!f.value &&
        (!Array.isArray(f.options) || f.options.length === 0)
      );
    });

    if (targets.length === 0) {
      isInitialHydrationRef.current = false;
      return;
    }

    // sequentially (same pattern you used elsewhere) to avoid race issues
    const run = async () => {
      let workingCopy: FieldProps[] = [...cygnusFormFields];

      for (const field of targets) {
        const apiKey = field.apiConfig!.apiKey!;
        const valueToSearch = String(field.value);

        // Guard: run once per field+value+apiKey for this mount
        const primeKey = `${field.id}:${apiKey}:${valueToSearch}`;
        if (primedTypeaheadRef.current.has(primeKey)) continue;
        primedTypeaheadRef.current.add(primeKey);

        // IMPORTANT:
        // handleApiDropdownSelection uses the field.value as the "search" term in typeahead mode.
        // We pass a shallow copy of the field, with value set to the saved id.
        const updatedField: FieldProps = { ...field, value: valueToSearch };

        await new Promise<void>((resolve) => {
          handleApiDropdownSelection(updatedField, apiKey, workingCopy, (updated) => {
            workingCopy = updated;
            // Wait for real API response (ignore spinner-only updates) to prevent label reset
            const wcField = workingCopy.find((x) => x.id === field.id);
            const stillLoading =
              wcField?.apiConfig?.isFetching === true &&
              (!wcField?.options || wcField.options.length === 0);

            // If this is just the spinner update, don't resolve yet
            if (stillLoading) return;
            resolve();
          });
        });

        // Merge the fetched options into state (without wiping the original value)
        setCygnusFormFields((prev) => mergeOptionsOnly(prev, workingCopy));
        isInitialHydrationRef.current = false;
      }
    };

    run();
    // We only want to react to first meaningful hydration; guard handles repeats anyway
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cygnusFormFields]);


  // inside CygnusForm.tsx
  const extractValue = (field: FieldProps, event: any) => {
    switch (field.type) {
      case "checkbox":
      case "slider":
        return event?.target?.checked ?? false;
      case "radio":
        return event?.value ?? event?.target?.value ?? field.value;
      case "select":
        return event?.value ?? "";
      case "file":
        return event?.target?.files?.[0] ?? event?.[0] ?? null;
      case "date":
        return event;
      default:
        return event?.target?.value ?? event;
    }
  };

  const dirtyRef = React.useRef<Record<string, true>>({});

  const markDirty = (id: string) => {
    const key = String(id);
    if (!dirtyRef.current[key]) {
      dirtyRef.current[key] = true;
      // optionally notify parent *now*
      onDirtyChange?.({
        ids: Object.keys(dirtyRef.current),
        byId: { ...dirtyRef.current },
      });
    }
  };

  const clearDirty = () => {
    dirtyRef.current = {};
    onDirtyChange?.({ ids: [], byId: {} });
  };

  useEffect(() => {
    provideDirtyApi?.({
      get: () => Object.keys(dirtyRef.current),
      has: (id: string) => !!dirtyRef.current[String(id)],
      size: () => Object.keys(dirtyRef.current).length,
      clear: clearDirty,
    });
    // call once on mount; if you need it to refresh, add deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevFormIdRef = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    // if your formId prop changes, it’s a fresh session
    if (formId !== prevFormIdRef.current) {
      prevFormIdRef.current = formId;
      clearDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  /**
   * Handles changes to individual cygnusFormFields using shared logic from utils.
   *
   * @param fieldId - ID of the field being updated
   * @param event - Input change event or value
   */
  const handleFieldChange = (fieldId: string, event: any) => {
    const fld = cygnusFormFields.find((f) => f.id === fieldId);
    if (!fld) {
      coreHandleChange(fieldId, event, cygnusFormFields, setCygnusFormFields);
      return;
    }

    const rawVal = extractValue(fld, event);

    const changed =
      JSON.stringify(rawVal) !== JSON.stringify(fld.value);
    if (changed && !isInitialHydrationRef.current) {
      markDirty(fieldId);
    }

    let formattedVal = rawVal;

    if (fld.type === "select" && fld.format) {
      formattedVal = applyNameFormat(String(rawVal), fld.format);

      if (event && typeof event === "object") {
        event.value = formattedVal;

        // Do not overwrite the label for typeahead selects.
        // Their label may be "ID - Name" and should stay as returned by the API.
        if (!fld.typeahead) {
          event.label = formattedVal;
        }
      }
    }

    if (fld?.emitOnChange && typeof onFieldEvent === "function") {
      onFieldEvent({
        eventKey: fld.onChangeEventKey,
        field: fld,
        value: formattedVal,
        allFields: cygnusFormFields,
        formId,
      });
    }
    coreHandleChange(fieldId, event, cygnusFormFields, (updatedFields) => {
      const shouldApplyRulesOnChange =
        fld.type === "select" ||
        fld.type === "checkbox" ||
        fld.type === "radio" ||
        fld.type === "slider";

      setCygnusFormFields(
        shouldApplyRulesOnChange
          ? applyIntakeRules(updatedFields)
          : updatedFields
      );
    });
  };

  /**
   * Handles input typing for typeahead dropdowns (without committing value changes).
   *
   * @param fieldId - ID of the field being typed into
   * @param value - The text entered by the user
   */
  const handleInputChange = (fieldId: string, value: string) => {
    const fld = cygnusFormFields.find((f) => f.id === fieldId);
    if (
      !fld ||
      fld.type !== "select" ||
      !fld.typeahead ||
      !fld.apiConfig?.apiKey ||
      typeof value !== "string"
    )
      return;

    const minChars = fld.typeaheadMinChars ?? 3;
    const limit = fld.fetchLimit ?? 30;

    if (value.length < minChars || limit <= 0) return;

    clearTimeout((fld as any)._typeaheadTimer);
    (fld as any)._typeaheadTimer = setTimeout(() => {
      const workingCopy = [...cygnusFormFields];
      const updatedField = { ...fld, value };

      handleApiDropdownSelection(
        updatedField,
        fld.apiConfig?.apiKey!,
        workingCopy,
        (newFields) => {
          const result = newFields.find((f) => f.id === fieldId);
          if (!result) return;
          setCygnusFormFields((prev) =>
            prev.map((f) => (f.id === fieldId ? result : f))
          );
        }
      );
    }, 400);
  };

  /**
   * Handles changes to individual cygnusFormFields using shared logic from utils.
   *
   * @param fieldId - ID of the field being updated
   * @param event - Input change event or value
   */
  const lastBlurredValuesRef = useRef<Record<string, any>>({});

  const handleFieldBlur = (fieldId: string) => {
    coreHandleBlur(fieldId, cygnusFormFields, setCygnusFormFields);
    setCygnusFormFields((prev) => applyIntakeRules(prev));

    const fld = cygnusFormFields.find((f) => f.id === fieldId);
    if (!fld?.emitOnBlur || typeof onFieldEvent !== "function") return;

    const last = lastBlurredValuesRef.current[fieldId];
    if (last === fld.value) return; // no change → no call
    lastBlurredValuesRef.current[fieldId] = fld.value;

    onFieldEvent({
      eventKey: fld.onBlurEventKey ?? fld.onChangeEventKey,
      field: fld,
      value: fld.value,
      allFields: cygnusFormFields,
      formId,
    });
  };

  // CygnusForm.tsx
  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== "Enter") return;

    const target = e.target as HTMLElement | null;
    if (!target) return;

    // Grab the input/textarea/select to read its id
    const el =
      (target.closest("input, textarea, select") as HTMLInputElement) ||
      undefined;
    const fieldId = el?.id;
    if (!fieldId) return;

    const fld = cygnusFormFields.find((f) => f.id === fieldId);
    if (!fld?.emitOnEnter) return;

    e.preventDefault(); // stop accidental default submit
    // (Optional) run blur validation when user hits Enter
    coreHandleBlur(fieldId, cygnusFormFields, setCygnusFormFields);

    if (typeof onFieldEvent === "function") {
      onFieldEvent({
        eventKey: fld.onEnterEventKey ?? fld.onChangeEventKey,
        field: fld,
        value: fld.value,
        allFields: cygnusFormFields,
        formId,
      });
    }
  };

  /**
   * Determines if a field should be visible based on its own `show` flag and visibility dependency.
   *
   * @param field - The current field being evaluated for visibility.
   * @param allFields - The full list of all fields (used to find dependency references).
   * @returns `true` if the field should be visible, otherwise `false`.
   */
  const shouldFieldBeVisible = (
    field: FieldProps,
    allFields: FieldProps[]
  ): boolean => {
    // Explicit hide wins
    if (field.show === false) return false;

    // No dependency configured
    if (!field.dependsOn) return true;

    // Build an expected-values array from either the new list or the old single value
    const expectedValues =
      Array.isArray(field.dependsOnVisibilityValues) &&
        field.dependsOnVisibilityValues.length > 0
        ? field.dependsOnVisibilityValues
        : field.dependsOnVisibilityValue !== undefined &&
          field.dependsOnVisibilityValue !== ""
          ? [field.dependsOnVisibilityValue]
          : undefined;

    // If no explicit expected values, default to visible
    if (!expectedValues || expectedValues.length === 0) return true;

    // Find parent by its allfields_code as you already do
    const parent = allFields.find((f) => f.allfields_code === field.dependsOn);

    // If parent is missing, hide to be safe
    if (!parent) return false;

    // If the parent itself is not visible, hide this field too (cascading visibility)
    if (!shouldFieldBeVisible(parent, allFields)) return false;

    const parentVal = parent.value;

    // If parent is an array (e.g., multi-select), succeed if any element matches any expected
    if (Array.isArray(parentVal)) {
      return parentVal.some((pv) => expectedValues.some((ev) => pv === ev));
    }

    // Parent is scalar: true if it matches any expected value
    return expectedValues.some((ev) => parentVal === ev);
  };

  const hasSectionVisibleFields = (blockFields: FieldProps[]): boolean => {
    return blockFields.some(
      (f) => !f.sectionEnd && shouldFieldBeVisible(f, cygnusFormFields)
    );
  };

  const unresolvedSystemFields = cygnusFormFields.filter(isUnmappedSystemField);

  const handleSystemFieldsResolved = (updatedFields: FieldProps[]) => {
    setCygnusFormFields(updatedFields);
    onFieldsChange?.(updatedFields);
    lastEmittedRef.current = updatedFields;
  };

  const collectSections = (fields: FieldProps[]) => {
    const output: {
      isSection: boolean;
      fields: FieldProps[];
      sectionName?: string;
      sectionStyles?: any;
    }[] = [];

    let buffer: FieldProps[] = [];
    let inSection = false;
    let sectionMeta: { name?: string; styles?: any } = {};

    for (const field of fields) {
      if (field.sectionStart) {
        if (buffer.length > 0 && !inSection) {
          // Only flush if the previous section was properly closed (or we're before
          // any section). If inSection is still true, the previous section never got
          // a sectionEnd — absorb its buffer into the new section instead.
          output.push({
            isSection: inSection,
            fields: buffer,
            sectionName: sectionMeta.name,
            sectionStyles: sectionMeta.styles,
          });
          buffer = [];
        }

        inSection = true;
        sectionMeta = {
          name: field.sectionName,
          styles: field.sectionStyles,
        };
      }

      buffer.push(field);

      if (field.sectionEnd) {
        if (inSection) {
          output.push({
            isSection: true,
            fields: buffer,
            sectionName: sectionMeta.name,
            sectionStyles: sectionMeta.styles,
          });
          buffer = [];
          inSection = false;
          sectionMeta = {};
        }
      }
    }

    // Flush leftover
    if (buffer.length > 0) {
      output.push({
        isSection: inSection,
        fields: buffer,
        sectionName: sectionMeta.name,
        sectionStyles: sectionMeta.styles,
      });
    }

    return output;
  };

  const renderField = (field: FieldProps) => {
    //   Keep for future checks    
    //   if (field?.label === "Existing Client Name") {
    //   console.log("EXISTING CLIENT NAME FIELD DEBUG:", {
    //     firmField: field,
    //     value: field.value,
    //     selectedOption: field.selectedOption,
    //     options: field.options,
    //     fetchedOptions: field.apiConfig?.fetchedOptions,
    //     format: field.format,
    //     apiKey: field.apiConfig?.apiKey,
    //     typeahead: field.typeahead,
    //     onChangeEventKey: field.onChangeEventKey,
    //     allfields_key: field.allfields_key,
    //     allfields_code: field.allfields_code,
    //   });
    // }

    return (
      shouldFieldBeVisible(field, cygnusFormFields) && (
        <React.Fragment key={field.id}>
          {field.breakLine && <div className={styles.breakLineSpacer} />}
          <InputFieldsController
            field={{
              ...field,
              disabled: disabled || field.disabled,
              ...(enableSystemFieldManagement && isUnmappedSystemField(field)
                ? {
                    onResolveSystemField: (id: string) => {
                      setSystemFieldDrawerFieldId(id);
                      setShowSystemFieldDrawer(true);
                    },
                  }
                : {}),
            }}
            handleChange={handleFieldChange}
            handleBlur={handleFieldBlur}
            handleSelect={() => { }}
            onInputChange={
              field.type === "select" && field.typeahead
                ? (id, value) => {
                  handleInputChange(id, value);
                }
                : undefined
            }
          />
        </React.Fragment>
      )
    );
  };

  return (
    <Form
      className={styles.cygnusForm}
      id={formId}
      onKeyDown={handleFormKeyDown}
      data-field-variant={fieldVariant ?? "default"}
      data-section-variant={sectionVariant ?? "default"}
    >
      <SystemFieldDrawer
        show={showSystemFieldDrawer}
        fields={cygnusFormFields}
        initialFieldId={systemFieldDrawerFieldId}
        systemFields={systemFields}
        categoryOptions={categoryOptions}
        fieldDataLoading={systemFieldDataLoading}
        onSystemFieldsRefresh={refreshSystemFields}
        onClose={() => setShowSystemFieldDrawer(false)}
        onFieldsResolved={handleSystemFieldsResolved}
      />
      {(pages.length > 1 || (enableSystemFieldManagement && unresolvedSystemFields.length > 0)) && (
        <div className={styles.formToolbar}>
          {pages.length > 1 && (
            viewMode === "all" ? (
            <Icon.Stack
              title="Switch to wizard view"
              role="button"
              aria-label="Switch"
              className={styles.controlButton}
              onClick={() =>
                setViewMode((prev) => (prev === "all" ? "paginated" : "all"))
              }
            />
            ) : (
            <Icon.TvFill
              title="Switch to scrollable view"
              role="button"
              aria-label="Switch"
              className={styles.controlButton}
              onClick={() =>
                setViewMode((prev) => (prev === "all" ? "paginated" : "all"))
              }
            />
            )
          )}
          {enableSystemFieldManagement && unresolvedSystemFields.length > 0 && (
            <button
              type="button"
              className={styles.systemFieldsButton}
              onClick={() => {
                setSystemFieldDrawerFieldId("");
                setShowSystemFieldDrawer(true);
              }}
              title="Resolve system fields"
            >
              <Icon.DatabaseAdd />
              <span>Resolve Fields</span>
              <span className={styles.systemFieldsCount}>
                {unresolvedSystemFields.length}
              </span>
            </button>
          )}
        </div>
      )}
      <div className={styles.formCanvas}>
        {viewMode === "all" ? (
          pages.map((pageFields, pageIndex) => (
            <div key={`page-${pageIndex}`} className={styles.pageSection}>
              <div
                className={
                  !pageIndex
                    ? styles.paginationControls
                    : styles.alteredPaginationControls
                }
              >
                <div className={styles.pageHeader}>
                  {pages.length > 1
                    ? pageFields.find(
                      (f, i) => f.pageBreak || (i === 0 && pageIndex === 0)
                    )?.pageName || `Page ${pageIndex + 1}`
                    : null}
                </div>
              </div>
              <div className={styles.pageContent}>
                {(() => {
                  const blocks = collectSections(pageFields);
                  let sectionCount = 0;
                  return blocks.map((block, blockIndex) => {
                    if (block.isSection) sectionCount++;
                    return block.isSection ? (
                      hasSectionVisibleFields(block.fields) ? (
                        <SectionWrapper
                          key={`section-${blockIndex}`}
                          sectionName={block.sectionName}
                          sectionLayout={sectionVariant === "flat" ? "plain" : sectionLayout}
                          sectionIndex={sectionCount}
                          customStyles={
                            sectionVariant === "flat"
                              ? { titleColor: block.sectionStyles?.titleColor }
                              : block.sectionStyles
                          }
                        >
                          {block.fields.map(renderField)}
                        </SectionWrapper>
                      ) : null
                    ) : (
                      block.fields.map(renderField)
                    );
                  });
                })()}
              </div>
            </div>
          ))
        ) : (
          <div className={styles.pageSection}>
            {pages.length > 1 ? (
              <div className={styles.paginationControls}>
                <CustomButton
                  onclick={() => handlePrev()}
                  disabled={currentPage === 0}
                  content={
                    <Icon.ArrowLeft
                      className={styles.wizardFormNavigationButton}
                    />
                  }
                />
                <div className={styles.pageHeader}>
                  {pages[currentPage]?.find(
                    (f, i) => f.pageBreak || (i === 0 && currentPage === 0)
                  )?.pageName || `Page ${currentPage + 1}`}
                </div>
                <CustomButton
                  onclick={() => handleNext()}
                  disabled={currentPage === pages.length - 1}
                  content={
                    <Icon.ArrowRight
                      className={styles.wizardFormNavigationButton}
                    />
                  }
                />
              </div>
            ) : (
              <></>
            )}
            <div className={styles.pageContent}>
              {(() => {
                const currentFields = pages[currentPage] || [];
                const blocks = collectSections(currentFields);
                let sectionCount = 0;
                return blocks.map((block, blockIndex) => {
                  if (block.isSection) sectionCount++;
                  return block.isSection ? (
                    <SectionWrapper
                      key={`section-${blockIndex}`}
                      sectionName={block.sectionName}
                      sectionLayout={sectionVariant === "flat" ? "plain" : sectionLayout}
                      sectionIndex={sectionCount}
                      customStyles={
                        sectionVariant === "flat"
                          ? { titleColor: block.sectionStyles?.titleColor }
                          : block.sectionStyles
                      }
                    >
                      {block.fields.map(renderField)}
                    </SectionWrapper>
                  ) : (
                    block.fields.map(renderField)
                  );
                });
              })()}
            </div>
          </div>
        )}
      </div>
    </Form>
  );
};

export default CygnusForm;
